import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "form", "textarea", "submitButton", "cancelButton", 
    "commentList", "comment", "editForm", "replyForm",
    "typeSelect", "internalCheckbox", "content", "loadingOverlay"
  ]
  static values = {
    commentableType: String,
    commentableId: Number,
    maxLength: { type: Number, default: 2000 },
    autoResize: { type: Boolean, default: true },
    enableKeyboardShortcuts: { type: Boolean, default: true }
  }

  connect() {
    this.setupEventListeners()
    this.setupKeyboardShortcuts()
    this.initializeAutoResize()
    this.updateCharacterCount()
    this.updateFormAppearance()
  }

  disconnect() {
    this.removeEventListeners()
  }

  // Enhanced form submission handling
  submitForm(event) {
    if (!this.validateForm()) {
      event.preventDefault()
      return false
    }
  }

  startSubmitting() {
    if (this.hasLoadingOverlayTarget) {
      this.loadingOverlayTarget.classList.remove("hidden")
    }
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
    }
  }

  endSubmitting() {
    if (this.hasLoadingOverlayTarget) {
      this.loadingOverlayTarget.classList.add("hidden")
    }
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = false
    }
  }

  validateForm() {
    if (!this.hasTextareaTarget) return true
    
    const content = this.textareaTarget.value.trim()
    const maxLength = this.maxLengthValue
    
    if (content.length === 0) {
      this.showValidationError("Please enter a comment before submitting.")
      return false
    }
    
    if (content.length > maxLength) {
      this.showValidationError(`Comment is too long. Maximum ${maxLength} characters allowed.`)
      return false
    }
    
    return true
  }

  showValidationError(message) {
    // Create or update error message
    let errorElement = this.element.querySelector('.validation-error')
    if (!errorElement) {
      errorElement = document.createElement('div')
      errorElement.className = 'validation-error text-sm text-red-600 mt-2 p-2 bg-red-50 border border-red-200 rounded-lg'
      this.textareaTarget.parentNode.appendChild(errorElement)
    }
    
    errorElement.textContent = message
    errorElement.style.display = 'block'
    
    // Remove error after 5 seconds
    setTimeout(() => {
      if (errorElement) {
        errorElement.style.display = 'none'
      }
    }, 5000)
  }

  hideValidationError() {
    const errorElement = this.element.querySelector('.validation-error')
    if (errorElement) {
      errorElement.style.display = 'none'
    }
  }

  updateFormAppearance() {
    if (!this.hasFormTarget) return
    
    const isInternal = this.hasInternalCheckboxTarget && this.internalCheckboxTarget.checked
    const commentType = this.hasTypeSelectTarget ? this.typeSelectTarget.value : 'general'
    
    // Update form border based on type and internal status
    const form = this.formTarget
    
    // Remove existing type classes
    form.classList.remove('border-red-300', 'border-amber-300', 'border-blue-300', 'border-emerald-300')
    
    if (isInternal) {
      form.classList.add('border-red-300', 'bg-gradient-to-r', 'from-red-50', 'to-red-100')
      form.classList.remove('from-slate-50', 'to-slate-100')
    } else {
      form.classList.remove('border-red-300', 'from-red-50', 'to-red-100')
      form.classList.add('from-slate-50', 'to-slate-100')
      
      // Add subtle color based on comment type
      switch(commentType) {
        case 'feedback':
          form.classList.add('border-amber-300')
          break
        case 'approval':
          form.classList.add('border-emerald-300')
          break
        case 'note':
          form.classList.add('border-blue-300')
          break
        default:
          // Keep default slate border
      }
    }
  }

  cancelForm() {
    this.clearForm()
    this.hideValidationError()
    this.updateFormAppearance()
    
    // Optional: Add a subtle animation
    if (this.hasFormTarget) {
      this.formTarget.style.transform = 'scale(0.98)'
      setTimeout(() => {
        this.formTarget.style.transform = 'scale(1)'
      }, 150)
    }
  }

  clearForm() {
    if (this.hasTextareaTarget) {
      this.textareaTarget.value = ""
      this.updateCharacterCount()
      this.resizeTextarea()
    }
    
    if (this.hasTypeSelectTarget) {
      this.typeSelectTarget.value = "general"
    }
    
    if (this.hasInternalCheckboxTarget) {
      this.internalCheckboxTarget.checked = false
    }
    
    this.updateFormAppearance()
  }

  hideForm() {
    this.element.classList.add("hidden")
  }

  showForm() {
    this.element.classList.remove("hidden")
    if (this.hasTextareaTarget) {
      this.textareaTarget.focus()
    }
  }

  // Character count and validation
  updateCharacterCount() {
    if (!this.hasTextareaTarget) return

    const textarea = this.textareaTarget
    const currentLength = textarea.value.length
    const maxLength = this.maxLengthValue
    
    // Find or create character count element
    let charCount = this.element.querySelector('.char-count')
    if (!charCount) {
      charCount = document.createElement('div')
      charCount.className = 'char-count text-xs text-slate-500 mt-2 flex items-center justify-between'
      charCount.innerHTML = `
        <span class="count-text"></span>
        <div class="flex items-center gap-1">
          <div class="progress-bar w-16 h-1 bg-slate-200 rounded-full overflow-hidden">
            <div class="progress-fill h-full transition-all duration-200 rounded-full"></div>
          </div>
        </div>
      `
      textarea.parentNode.appendChild(charCount)
    }
    
    const countText = charCount.querySelector('.count-text')
    const progressFill = charCount.querySelector('.progress-fill')
    const percentage = (currentLength / maxLength) * 100
    
    countText.textContent = `${currentLength}/${maxLength}`
    progressFill.style.width = `${Math.min(percentage, 100)}%`
    
    // Update styling based on length
    charCount.classList.remove('text-slate-500', 'text-amber-600', 'text-red-600')
    progressFill.classList.remove('bg-slate-400', 'bg-amber-500', 'bg-red-500')
    
    if (currentLength > maxLength) {
      charCount.classList.add('text-red-600')
      progressFill.classList.add('bg-red-500')
    } else if (currentLength > maxLength * 0.9) {
      charCount.classList.add('text-amber-600')
      progressFill.classList.add('bg-amber-500')
    } else {
      charCount.classList.add('text-slate-500')
      progressFill.classList.add('bg-slate-400')
    }
    
    // Disable submit button if over limit or empty
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = currentLength > maxLength || currentLength === 0
    }
    
    // Hide validation error if user is typing
    if (currentLength > 0 && currentLength <= maxLength) {
      this.hideValidationError()
    }
  }

  // Auto-resize textarea
  initializeAutoResize() {
    if (this.autoResizeValue && this.hasTextareaTarget) {
      this.resizeTextarea()
    }
  }

  resizeTextarea() {
    if (!this.hasTextareaTarget) return

    const textarea = this.textareaTarget
    textarea.style.height = 'auto'
    const newHeight = Math.min(Math.max(textarea.scrollHeight, 96), 200) // Min 96px, max 200px
    textarea.style.height = newHeight + 'px'
  }

  // Edit functionality
  editComment(event) {
    event.preventDefault()
    const commentId = event.currentTarget.dataset.commentId
    const content = event.currentTarget.dataset.commentContent
    const commentElement = document.getElementById(`comment_${commentId}`)
    
    if (commentElement) {
      const contentElement = commentElement.querySelector('.comment-content')
      
      // Check if edit form already exists
      if (commentElement.querySelector('.edit-form')) {
        return
      }
      
      // Create edit form
      const editForm = this.createEditForm(commentId, content)
      
      // Replace comment content with edit form
      contentElement.style.display = 'none'
      contentElement.parentNode.insertBefore(editForm, contentElement.nextSibling)
      
      // Focus on textarea
      const textarea = editForm.querySelector('textarea')
      if (textarea) {
        textarea.focus()
        textarea.setSelectionRange(textarea.value.length, textarea.value.length)
      }
    }
  }

  createEditForm(commentId, content) {
    // Detect if we're in the worker app context
    const isWorkerApp = window.location.pathname.includes('/worker-app')
    const commentPath = isWorkerApp ? '/worker-app/comments' : '/comments'
    
    const form = document.createElement('div')
    form.className = 'edit-form bg-amber-50 rounded-lg p-4 border border-amber-200 mt-2 shadow-sm'
    form.innerHTML = `
      <form action="${commentPath}/${commentId}" method="post" data-turbo="true" class="space-y-3">
        <input type="hidden" name="_method" value="patch">
        <input type="hidden" name="authenticity_token" value="${document.querySelector('meta[name="csrf-token"]').content}">
        
        <div>
          <label class="block text-sm font-medium text-slate-700 mb-1">Edit comment</label>
          <textarea name="comment[content]" rows="3" 
                    class="w-full px-3 py-2 border border-amber-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 resize-none transition-all duration-200"
                    maxlength="${this.maxLengthValue}">${content}</textarea>
        </div>
        
        <div class="flex items-center gap-3">
          <button type="submit" class="px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 transition-colors">
            Update
          </button>
          <button type="button" class="px-4 py-2 text-slate-600 text-sm font-medium rounded-lg hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 transition-colors" 
                  data-action="click->comment#cancelEdit">
            Cancel
          </button>
        </div>
      </form>
    `
    
    return form
  }

  cancelEdit(event) {
    event.preventDefault()
    const editForm = event.target.closest('.edit-form')
    const commentElement = editForm.closest('[id^="comment_"]')
    const contentElement = commentElement.querySelector('.comment-content')
    
    contentElement.style.display = 'block'
    editForm.remove()
  }

  // Reply functionality
  showReplyForm(event) {
    event.preventDefault()
    const commentId = event.currentTarget.dataset.commentId
    const commentElement = document.getElementById(`comment_${commentId}`)
    
    if (commentElement) {
      // Hide any existing reply forms
      this.hideAllReplyForms()
      
      // Create reply form
      const replyForm = this.createReplyForm(commentId)
      
      // Find the turbo frame for replies within the comment
      const replyFrame = commentElement.querySelector(`turbo-frame[id="reply_form_${commentId}"]`)
      if (replyFrame) {
        // Clear any existing content and append the reply form
        replyFrame.innerHTML = ''
        replyFrame.appendChild(replyForm)
      } else {
        // Fallback: add reply form to the comment element
        commentElement.appendChild(replyForm)
      }
      
      // Focus on textarea
      const textarea = replyForm.querySelector('textarea')
      if (textarea) {
        textarea.focus()
      }
    }
  }

  createReplyForm(parentCommentId) {
    // Detect if we're in the worker app context
    const isWorkerApp = window.location.pathname.includes('/worker-app')
    const commentPath = isWorkerApp ? '/worker-app/comments' : '/comments'
    
    const form = document.createElement('div')
    form.className = 'reply-form mt-4 animate-fadeIn'
    form.innerHTML = `
      <div class="ml-6 relative">
        <div class="absolute -left-6 top-3 w-4 h-px bg-gray-200"></div>
        <div class="flex gap-x-3">
          <div class="relative mt-1 size-5 flex-none rounded-full bg-gray-50 flex items-center justify-center">
            <span class="text-xs font-medium text-gray-600">
              ${this.getCurrentUserInitials()}
            </span>
          </div>
          <div class="flex-auto min-w-0">
            <form action="${commentPath}" method="post" data-turbo="true" class="space-y-3">
              <input type="hidden" name="authenticity_token" value="${document.querySelector('meta[name="csrf-token"]').content}">
              <input type="hidden" name="comment[commentable_type]" value="${this.commentableTypeValue}">
              <input type="hidden" name="comment[commentable_id]" value="${this.commentableIdValue}">
              <input type="hidden" name="comment[reply_to]" value="${parentCommentId}">
              
              <div class="bg-white rounded-lg border border-gray-200 shadow-sm">
                <textarea name="comment[content]" rows="3" 
                          class="w-full px-3 py-2 border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none text-sm"
                          placeholder="Write your reply..."
                          maxlength="${this.maxLengthValue}"></textarea>
                
                <div class="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-b-lg border-t border-gray-200">
                  <div class="text-xs text-gray-500">
                    Replying to comment
                  </div>
                  <div class="flex items-center gap-2">
                    <button type="button" class="px-3 py-1.5 text-gray-600 text-sm font-medium rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors" 
                            data-action="click->comment#cancelReply">
                      Cancel
                    </button>
                    <button type="submit" class="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors">
                      Reply
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    `
    
    return form
  }

  getCurrentUserInitials() {
    // Try to get initials from an existing avatar in the page
    const existingAvatar = document.querySelector('.size-6 .text-xs')
    if (existingAvatar && existingAvatar.textContent.trim()) {
      return existingAvatar.textContent.trim()
    }
    
    // Fallback to generic initials
    return '?'
  }

  cancelReply(event) {
    event.preventDefault()
    const replyForm = event.target.closest('.reply-form')
    if (replyForm) {
      replyForm.remove()
    }
  }

  hideAllReplyForms() {
    const replyForms = this.element.querySelectorAll('.reply-form')
    replyForms.forEach(form => form.remove())
  }

  // Keyboard shortcuts
  setupKeyboardShortcuts() {
    if (!this.enableKeyboardShortcutsValue) return

    document.addEventListener('keydown', this.handleKeyboardShortcut.bind(this))
  }

  handleKeyboardShortcut(event) {
    // Ctrl/Cmd + Enter to submit forms
    if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
      const activeForm = document.querySelector('form:focus-within')
      if (activeForm && activeForm.closest('[data-controller*="comment"]')) {
        event.preventDefault()
        activeForm.requestSubmit()
      }
    }
    
    // Escape to cancel forms
    if (event.key === 'Escape') {
      const activeEditForm = document.querySelector('.edit-form:focus-within')
      if (activeEditForm) {
        event.preventDefault()
        this.cancelEdit({ preventDefault: () => {}, target: activeEditForm.querySelector('[data-action*="cancelEdit"]') })
      }
      
      const activeReplyForm = document.querySelector('.reply-form:focus-within')
      if (activeReplyForm) {
        event.preventDefault()
        this.cancelReply({ preventDefault: () => {}, target: activeReplyForm.querySelector('[data-action*="cancelReply"]') })
      }
    }
  }

  // Event listeners
  setupEventListeners() {
    if (this.hasTextareaTarget) {
      this.textareaTarget.addEventListener('input', this.updateCharacterCount.bind(this))
      this.textareaTarget.addEventListener('input', this.resizeTextarea.bind(this))
    }
  }

  removeEventListeners() {
    document.removeEventListener('keydown', this.handleKeyboardShortcut)
  }

  // Utility methods
  scrollToComment(commentId) {
    const commentElement = document.getElementById(`comment_${commentId}`)
    if (commentElement) {
      commentElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
      
      // Add highlight effect
      commentElement.classList.add('ring-2', 'ring-blue-500', 'ring-opacity-50')
      setTimeout(() => {
        commentElement.classList.remove('ring-2', 'ring-blue-500', 'ring-opacity-50')
      }, 2000)
    }
  }

  // Turbo Stream handlers
  afterCommentCreate(event) {
    const commentId = event.detail.commentId
    this.clearForm()
    this.scrollToComment(commentId)
  }

  afterCommentUpdate(event) {
    const commentId = event.detail.commentId
    this.scrollToComment(commentId)
  }

  afterCommentDelete(event) {
    // Comment was deleted, no action needed
  }
} 